import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Animated,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useEvent } from "expo"
import { Image } from "expo-image"
import { router, useFocusEffect, useLocalSearchParams } from "expo-router"
import { StatusBar } from "expo-status-bar"
import { useVideoPlayer, VideoView } from "expo-video"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { Document, Page, pdfjs } from "react-pdf"

import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

import type { YouTubeWebEmbedRef } from "src/components/YouTubeWebEmbed/types"
import { CompositeInputCluster } from "src/components/CompositeInputCluster"
import { LoginOrRequestAccessModal } from "src/components/ContentLibrary/LoginOrRequestAccessModal"
import { UnauthAvatarOverlay } from "src/components/ContentLibrary/UnauthAvatarOverlay"
import { Text } from "src/components/Ignite/Text"
import { MediaPlayerShell } from "src/components/MediaPlayerShell"
import { WebView } from "src/components/WebView"
import { YouTubeWebEmbed } from "src/components/YouTubeWebEmbed"
import { AvatarMode, useAvatarMode } from "src/context/AvatarModeContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { WEB_CANVAS_MAX_WIDTH } from "src/hooks/useWebLayoutConstraints"
import { useWebMouseActivity } from "src/hooks/useWebMouseActivity"
import { useWebViewNavigation } from "src/hooks/useWebViewNavigation"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { ThemedStyle } from "src/theme"
import { storage } from "src/utils/storage/mmkv"
import { getYouTubeVideoId } from "src/utils/youtubeUtils"

if (pdfjs?.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
}

type MediaType = "video" | "pdf" | "audio" | "image" | "url" | "youtube" | "unknown"

export default function UnauthMediaViewerScreen() {
  const params = useLocalSearchParams<{
    channelId: string
    taskId?: string
    mediaType?: string
    url?: string
    title?: string
    thumbnailUrl?: string
  }>()

  const channelId = params.channelId ?? ""
  const taskId = params.taskId
  const mediaType = (params.mediaType ?? "unknown") as MediaType
  const url = params.url ?? ""
  const title = params.title ?? ""
  const thumbnailUrl = params.thumbnailUrl ?? ""

  const { themed, theme } = useAppTheme()
  const { colors } = theme

  const themedAlert = useMemo(
    () => ({
      card: { backgroundColor: colors.background, shadowColor: colors.shadow },
      title: { color: colors.text },
      body: { color: colors.textDim },
      button: { backgroundColor: colors.buttonBackground },
      buttonText: { color: colors.menuButtonText },
    }),
    [colors],
  )
  const insets = useSafeAreaInsets()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const { isSpeaking, stopSpeech } = useVisemes()
  const { mode: avatarMode, setMode: setAvatarMode } = useAvatarMode()
  const avatarModeRef = useRef<AvatarMode>(avatarMode)
  avatarModeRef.current = avatarMode
  const sizeBeforePlayRef = useRef<AvatarMode>("minimized")

  const { data: channelInfo } = useUnauthChannelInfoQuery(channelId)
  const subscriptionUrl = channelInfo?.subscription_url ?? null

  const [lockModalVisible, setLockModalVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [embeddingError, setEmbeddingError] = useState(false)
  // Proactively offer to open in a new tab for website embeds, since many sites
  // block cross-origin iframes and we can't detect that reliably from the parent.
  const [showNewTabAlert, setShowNewTabAlert] = useState(mediaType === "url")

  // ── WebView navigation ─────────────────────────────────────────────────────
  const webViewRef = useRef<any>(null)
  const { canGoBack, canGoForward, goBack, goForward, webViewProps } =
    useWebViewNavigation(webViewRef)

  // ── PDF state ──────────────────────────────────────────────────────────────
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1)
  const [pdfHasError, setPdfHasError] = useState(false)
  const [toolbarVisible, setToolbarVisible] = useState(true)
  const toolbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
    } else {
      router.replace({
        pathname: "/unauth/channel-library/[id]",
        params: { id: channelId },
      } as any)
    }
  }, [channelId])

  const handleTalkTypePress = useCallback(() => {
    setLockModalVisible(true)
  }, [])

  const handleLoginPress = useCallback(() => {
    router.replace("/unauth/login")
  }, [])

  useFocusEffect(
    useCallback(() => {
      return () => {
        stopSpeech()
      }
    }, [stopSpeech]),
  )

  const isVideoType = mediaType === "video"
  const isAudioType = mediaType === "audio"
  const isImageType = mediaType === "image"
  const isPdfType = mediaType === "pdf"
  const isWebsiteType = mediaType === "url"
  const isYouTube = mediaType === "youtube"
  const youTubeVideoId = isYouTube ? (getYouTubeVideoId(url) ?? "") : ""
  const webPlayerRef = useRef<YouTubeWebEmbedRef>(null)
  const [_isYouTubePlaying, setIsYouTubePlaying] = useState(false)

  // Compute a contained 16:9 player size from the available viewport, capped to the web canvas
  const youtubePlayerSize = useMemo(() => {
    const paddingHorizontal = 32
    const canvasWidth = Math.min(windowWidth, WEB_CANVAS_MAX_WIDTH)
    const availableWidth = canvasWidth - paddingHorizontal
    const availableHeight = windowHeight
    const byWidth = { width: availableWidth, height: availableWidth * (9 / 16) }
    const byHeight = { width: availableHeight * (16 / 9), height: availableHeight }
    return byWidth.height <= availableHeight ? byWidth : byHeight
  }, [windowWidth, windowHeight])
  // expo-video player is only used for non-YouTube video and audio
  const isPlayable = isVideoType || isAudioType

  // ── Video / Audio player ───────────────────────────────────────────────────
  const player = useVideoPlayer(isPlayable ? url : "", (p) => {
    p.loop = false
    p.timeUpdateEventInterval = 0.1
    p.pause()
  })

  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [, setScrubTime] = useState<number | null>(null)
  const [isWebFullscreen, setIsWebFullscreen] = useState(false)
  const pendingSeekRef = useRef<number | null>(null)

  const toggleWebFullscreen = useCallback(() => setIsWebFullscreen((v) => !v), [])

  const { isPlaying } = useEvent(player, "playingChange", { isPlaying: player.playing })
  const { status: playerStatus } = useEvent(player, "statusChange", { status: player.status })

  // Show the thumbnail_url poster over the video until it actually starts
  // playing, so the user never sees the raw first frame while it loads.
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  useEffect(() => {
    if (isPlaying) {
      setHasStartedPlaying(true)
    }
  }, [isPlaying])

  const introBenefitsKey =
    channelId && taskId ? `unauth.cmBenefitsShown.${channelId}.${taskId}` : null
  const isFirstVisitForTaskRef = useRef(
    !!introBenefitsKey && storage.getBoolean(introBenefitsKey) !== true,
  )
  const [waitingForIntro, setWaitingForIntro] = useState(isFirstVisitForTaskRef.current)
  const introStartedRef = useRef(false)
  const introFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!waitingForIntro) return
    if (isSpeaking) {
      introStartedRef.current = true
      if (introFallbackRef.current) {
        clearTimeout(introFallbackRef.current)
        introFallbackRef.current = null
      }
    } else if (introStartedRef.current) {
      setWaitingForIntro(false)
    }
  }, [isSpeaking, waitingForIntro])

  useEffect(() => {
    if (!waitingForIntro) return
    introFallbackRef.current = setTimeout(() => setWaitingForIntro(false), 5000)
    return () => {
      if (introFallbackRef.current) {
        clearTimeout(introFallbackRef.current)
        introFallbackRef.current = null
      }
    }
  }, [waitingForIntro])

  const hasAutoplayed = useRef(false)
  useEffect(() => {
    if (!isPlayable || playerStatus !== "readyToPlay" || waitingForIntro || hasAutoplayed.current)
      return
    hasAutoplayed.current = true
    player.play()
  }, [isPlayable, playerStatus, waitingForIntro, player])

  const { duration: loadedDuration } = useEvent(player, "sourceLoad", {
    videoSource: null,
    duration: player.duration ?? 0,
    availableVideoTracks: [],
    availableSubtitleTracks: [],
    availableAudioTracks: [],
  })
  const { currentTime } = useEvent(player, "timeUpdate", {
    currentTime: player.currentTime ?? 0,
    currentLiveTimestamp: null,
    currentOffsetFromLive: null,
    bufferedPosition: player.bufferedPosition ?? 0,
  })

  useEffect(() => {
    if (!isPlayable) return
    if (loadedDuration && Number.isFinite(loadedDuration) && loadedDuration > 0) {
      setDuration((current) =>
        Math.abs(current - loadedDuration) > 0.1 ? loadedDuration : current,
      )
      return
    }
    if (playerStatus === "readyToPlay") {
      const nativeDuration = player.duration ?? 0
      if (nativeDuration > 0 && Number.isFinite(nativeDuration)) {
        setDuration((current) =>
          Math.abs(current - nativeDuration) > 0.1 ? nativeDuration : current,
        )
      }
    }
  }, [isPlayable, loadedDuration, playerStatus, player])

  useEffect(() => {
    if (!isPlayable) return
    if (!isScrubbing && typeof currentTime === "number" && !Number.isNaN(currentTime)) {
      if (pendingSeekRef.current !== null) {
        if (Math.abs(currentTime - pendingSeekRef.current) < 0.5) {
          pendingSeekRef.current = null
        }
      }
      if (pendingSeekRef.current === null) {
        setPosition(currentTime)
      }
    }
  }, [isPlayable, currentTime, isScrubbing])

  const { isMouseActive, handleMouseMove } = useWebMouseActivity()
  // Show controls when paused, or when mouse is active while playing
  const shouldShowControls = !(isPlayable && isPlaying) || isMouseActive

  const controlsOpacity = useRef(new Animated.Value(1)).current

  useEffect(() => {
    Animated.timing(controlsOpacity, {
      toValue: shouldShowControls ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [shouldShowControls, controlsOpacity])

  useEffect(() => {
    if (!isPlayable) return
    if (isPlaying) {
      if (avatarModeRef.current !== "tab") {
        sizeBeforePlayRef.current = avatarModeRef.current
      }
      setAvatarMode("tab", false)
    } else {
      if (avatarModeRef.current === "tab") {
        setAvatarMode(sizeBeforePlayRef.current, false)
      }
    }
  }, [isPlaying, isPlayable, setAvatarMode])

  const overlayOpacity = useRef(new Animated.Value(0)).current
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [overlayIcon, setOverlayIcon] = useState<"play-circle-filled" | "pause-circle">(
    "play-circle-filled",
  )
  const currentAnimationToken = useRef<object | null>(null)

  const flashOverlay = useCallback(
    (willPlay: boolean) => {
      const animationToken = {}
      currentAnimationToken.current = animationToken
      overlayOpacity.stopAnimation(() => {
        setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
        overlayOpacity.setValue(0.75)
        setIsOverlayVisible(true)
        Animated.timing(overlayOpacity, {
          toValue: 0,
          duration: 800,
          delay: 1500,
          useNativeDriver: true,
        }).start(({ finished }) => {
          if (finished && currentAnimationToken.current === animationToken) {
            setIsOverlayVisible(false)
          }
        })
      })
    },
    [overlayOpacity],
  )

  const togglePause = useCallback(() => {
    if (!isPlayable) return
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      stopSpeech()
      hasAutoplayed.current = true
      player.play()
    }
    flashOverlay(willPlay)
  }, [isPlayable, isPlaying, player, stopSpeech, flashOverlay])

  // ── PDF helpers ────────────────────────────────────────────────────────────
  const resetToolbarTimeout = useCallback(() => {
    setToolbarVisible(true)
    if (toolbarTimeoutRef.current) clearTimeout(toolbarTimeoutRef.current)
    toolbarTimeoutRef.current = setTimeout(() => setToolbarVisible(false), 3000)
  }, [])

  useEffect(() => {
    if (!isLoading && numPages > 0) resetToolbarTimeout()
    return () => {
      if (toolbarTimeoutRef.current) clearTimeout(toolbarTimeoutRef.current)
    }
  }, [isLoading, numPages, resetToolbarTimeout])

  const onDocumentLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
    setNumPages(total)
    setIsLoading(false)
    setPdfHasError(false)
  }, [])

  const onDocumentLoadError = useCallback(() => {
    setIsLoading(false)
    setPdfHasError(true)
  }, [])

  const goToPrevPage = useCallback(() => setPageNumber((p) => Math.max(1, p - 1)), [])
  const goToNextPage = useCallback(
    () => setPageNumber((p) => Math.min(numPages, p + 1)),
    [numPages],
  )
  const zoomIn = useCallback(() => setScale((s) => Math.min(3, s + 0.25)), [])
  const zoomOut = useCallback(() => setScale((s) => Math.max(0.5, s - 0.25)), [])

  // Keyboard navigation for PDF
  useEffect(() => {
    if (!isPdfType) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault()
        setPageNumber((p) => Math.max(1, p - 1))
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault()
        setPageNumber((p) => Math.min(numPages, p + 1))
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault()
        setScale((s) => Math.min(3, s + 0.25))
      } else if (e.key === "-") {
        e.preventDefault()
        setScale((s) => Math.max(0.5, s - 0.25))
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isPdfType, numPages])

  const handleNewTabAlertOpen = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer")
    setShowNewTabAlert(false)
  }, [url])

  const handleNewTabAlertDismiss = useCallback(() => {
    setShowNewTabAlert(false)
  }, [])

  // ── Content renderer ───────────────────────────────────────────────────────
  const renderContent = () => {
    if (isYouTube && youTubeVideoId) {
      return (
        <View style={styles.youtubeStage}>
          <View
            style={{
              width: youtubePlayerSize.width,
              height: youtubePlayerSize.height,
              backgroundColor: "#000000",
            }}
          >
            <YouTubeWebEmbed
              ref={webPlayerRef}
              videoId={youTubeVideoId}
              startTime={0}
              onPlay={() => setIsYouTubePlaying(true)}
              onPause={() => setIsYouTubePlaying(false)}
              onEnd={() => setIsYouTubePlaying(false)}
              onError={() => {}}
            />
          </View>
        </View>
      )
    }

    if (isVideoType && url) {
      return (
        <>
          <VideoView
            player={player}
            style={[styles.absoluteFill, { width: "100%", height: "100%" } as unknown as ViewStyle]}
            contentFit="contain"
            nativeControls={false}
            allowsFullscreen
            allowsPictureInPicture={false}
            pointerEvents="none"
            crossOrigin="anonymous"
          />

          {/* Thumbnail poster shown over the video until playback begins, so
              the user sees thumbnail_url instead of the raw first frame. */}
          {!!thumbnailUrl && !hasStartedPlaying && (
            <Image
              source={{ uri: thumbnailUrl }}
              style={styles.absoluteFill}
              contentFit="contain"
              pointerEvents="none"
            />
          )}
        </>
      )
    }

    if (isAudioType && url) {
      return (
        <>
          <VideoView
            player={player}
            style={styles.hiddenVideoView}
            nativeControls={false}
            contentFit="contain"
            allowsFullscreen={false}
            allowsPictureInPicture={false}
            pointerEvents="none"
            crossOrigin="anonymous"
          />
          <View style={styles.audioContainer} pointerEvents="none">
            <MaterialIcons name="volume-up" size={120} color={colors.text} />
            {!!title && (
              <Text style={[styles.audioTitle, { color: colors.text }]} numberOfLines={2}>
                {title}
              </Text>
            )}
          </View>
        </>
      )
    }

    if (isImageType && url) {
      return (
        <Image
          source={{ uri: url }}
          style={styles.absoluteFill}
          contentFit="contain"
          onLoad={() => setIsLoading(false)}
        />
      )
    }

    if (isPdfType && url) {
      return (
        <View style={styles.pdfOuterContainer}>
          {isLoading && (
            <View style={styles.pdfLoadingContainer}>
              <MaterialIcons name="hourglass-empty" size={32} color="#FFFFFF" />
              <Text style={[styles.pdfLoadingText, { color: "#FFFFFF" }]}>Loading PDF...</Text>
            </View>
          )}
          {pdfHasError && (
            <View style={styles.pdfErrorOverlay}>
              <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
              <Text style={[styles.pdfErrorTitle, { color: "#FFFFFF" }]}>Failed to Load PDF</Text>
              <Text style={[styles.pdfErrorText, { color: "#FFFFFF" }]}>
                The PDF could not be loaded. Please check your connection and try again.
              </Text>
            </View>
          )}
          <div style={webStyles.scrollContainer} onMouseMove={resetToolbarTimeout}>
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=""
              error=""
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                width={Math.min(windowWidth - 80, 900)}
                renderAnnotationLayer
                renderTextLayer
              />
            </Document>
          </div>
          {!pdfHasError && numPages > 0 && (
            <div
              style={{
                ...webStyles.toolbar,
                opacity: toolbarVisible ? 1 : 0,
                pointerEvents: (toolbarVisible ? "auto" : "none") as "auto" | "none",
                transition: "opacity 0.3s ease",
              }}
              onMouseEnter={() => {
                if (toolbarTimeoutRef.current) clearTimeout(toolbarTimeoutRef.current)
                setToolbarVisible(true)
              }}
              onMouseLeave={resetToolbarTimeout}
            >
              <div style={webStyles.toolbarSection}>
                <button
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  style={webStyles.toolbarButton}
                  title="Zoom out (-)"
                >
                  <MaterialIcons name="remove" size={18} color="#FFFFFF" />
                </button>
                <span style={webStyles.zoomLabel}>{Math.round(scale * 100)}%</span>
                <button
                  onClick={zoomIn}
                  disabled={scale >= 3}
                  style={webStyles.toolbarButton}
                  title="Zoom in (+)"
                >
                  <MaterialIcons name="add" size={18} color="#FFFFFF" />
                </button>
              </div>
              <div style={webStyles.toolbarSection}>
                <button
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  style={webStyles.toolbarButton}
                  title="Previous page (←)"
                >
                  <MaterialIcons name="chevron-left" size={22} color="#FFFFFF" />
                </button>
                <span style={webStyles.pageLabel}>
                  {pageNumber} / {numPages}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  style={webStyles.toolbarButton}
                  title="Next page (→)"
                >
                  <MaterialIcons name="chevron-right" size={22} color="#FFFFFF" />
                </button>
              </div>
            </div>
          )}
        </View>
      )
    }

    if (isWebsiteType && url) {
      return (
        <>
          <WebView
            ref={webViewRef}
            source={{ uri: url }}
            style={styles.absoluteFill}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
            onEmbeddingError={() => {
              setIsLoading(false)
              setEmbeddingError(true)
            }}
            {...webViewProps}
          />
          {embeddingError && (
            <View style={styles.embeddingErrorOverlay}>
              <View style={[styles.embeddingErrorCard, themedAlert.card]}>
                <Text style={[styles.embeddingErrorTitle, themedAlert.title]}>
                  This website can&apos;t be displayed here
                </Text>
                <Text style={[styles.embeddingErrorBody, themedAlert.body]}>
                  This website has blocked embedding. You can open it in a new tab instead.
                </Text>
                <View style={styles.embeddingErrorActions}>
                  <TouchableOpacity
                    onPress={() => window.open(url, "_blank", "noopener,noreferrer")}
                    style={[styles.embeddingErrorButton, themedAlert.button]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                      Open in New Tab
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleBack}
                    style={[styles.embeddingErrorButton, themedAlert.button]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                      Close
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
          {showNewTabAlert && (
            <View style={styles.embeddingErrorOverlay}>
              <View style={[styles.embeddingErrorCard, themedAlert.card]}>
                <Text style={[styles.embeddingErrorTitle, themedAlert.title]}>
                  This website may require opening in a new tab.
                </Text>
                <View style={styles.embeddingErrorActions}>
                  <TouchableOpacity
                    onPress={handleNewTabAlertOpen}
                    style={[styles.embeddingErrorButton, themedAlert.button]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                      Open in a new tab
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleNewTabAlertDismiss}
                    style={[styles.embeddingErrorButton, themedAlert.button]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                      Attempt to view here
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </>
      )
    }

    return (
      <View style={styles.unsupportedContainer}>
        <MaterialIcons name="error-outline" size={48} color={colors.textDim} />
        <Text style={themed($unsupportedText)}>This content type is not supported.</Text>
      </View>
    )
  }

  const topRightButtons = (
    <View style={styles.topRightRow}>
      <TouchableOpacity
        onPress={handleLoginPress}
        style={[styles.compactLoginButton, { backgroundColor: colors.buttonBackground }]}
        accessibilityRole="button"
        accessibilityLabel="Log In"
      >
        <MaterialIcons name="login" size={18} color={colors.buttonText} />
        <Text style={[styles.compactLoginLabel, { color: colors.buttonText }]}>Log In</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={handleBack}
        style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
        accessibilityRole="button"
        accessibilityLabel="Close"
      >
        <MaterialIcons name="close" size={20} color={colors.buttonText} />
      </TouchableOpacity>
    </View>
  )

  // No style prop — bottomSlotFullWidth on the shell wrapper already provides
  // flex:1 width and alignItems:stretch. Passing flex:1 here would set
  // flexBasis:0 (Yoga shorthand), overriding the explicit height:56 and
  // collapsing the pill.
  const talkTypePill = <CompositeInputCluster onInteractionBlocked={handleTalkTypePress} />

  const showLoadingOverlay = isLoading && (isImageType || isWebsiteType) && !!url

  return (
    <View style={styles.container} {...({ onMouseMove: handleMouseMove } as any)}>
      <StatusBar style="light" />

      <MediaPlayerShell
        darkBackground
        keepControlsOnKeyboard
        suppressBuiltInLeftControls
        showControlsGradient={false}
        controlsOpacity={controlsOpacity}
        controlsVisible={shouldShowControls}
        onContentPress={isPlayable ? togglePause : undefined}
        topRightAccessory={topRightButtons}
        avatarSlot={
          <UnauthAvatarOverlay channelId={channelId} taskId={taskId} isMediaPlaying={isPlaying} />
        }
        controlsConfig={
          isPlayable
            ? {
                type: "playback",
                isPlaying,
                onTogglePlay: togglePause,
                position,
                duration,
                isScrubbing,
                onScrubStart: () => {
                  setIsScrubbing(true)
                  setScrubTime(position)
                },
                onScrubChange: (value) => {
                  setPosition(value)
                  setScrubTime(value)
                },
                onScrubEnd: (value) => {
                  setIsScrubbing(false)
                  setScrubTime(null)
                  setPosition(value)
                  pendingSeekRef.current = value
                  player.currentTime = value
                },
                isFullscreen: isWebFullscreen,
                onToggleFullscreen: toggleWebFullscreen,
              }
            : isWebsiteType
              ? {
                  type: "navigation",
                  canGoBack,
                  canGoForward,
                  onGoBack: goBack,
                  onGoForward: goForward,
                }
              : undefined
        }
        bottomSlot={talkTypePill}
      >
        {renderContent()}
      </MediaPlayerShell>

      {showLoadingOverlay && (
        <View
          style={[styles.loadingOverlay, { paddingBottom: insets.bottom + 80 }]}
          pointerEvents="none"
        >
          <MaterialIcons name="hourglass-empty" size={48} color={colors.textDim} />
        </View>
      )}

      {isOverlayVisible && (
        <Animated.View
          pointerEvents="none"
          style={[styles.fullscreenOverlay, { opacity: overlayOpacity }]}
        >
          <MaterialIcons name={overlayIcon} size={120} color="#FFFFFF" accessibilityRole="image" />
        </Animated.View>
      )}

      <LoginOrRequestAccessModal
        visible={lockModalVisible}
        onDismiss={() => setLockModalVisible(false)}
        subscriptionUrl={subscriptionUrl}
        channelId={channelId}
      />
    </View>
  )
}

const webStyles: Record<string, React.CSSProperties> = {
  scrollContainer: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "16px 0",
    width: "100%",
    height: "100%",
  },
  toolbar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 32,
    padding: "10px 16px",
    backgroundColor: "rgba(30, 30, 30, 0.9)",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
  },
  toolbarSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toolbarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    cursor: "pointer",
    padding: 0,
  },
  pageLabel: {
    color: "#FFFFFF",
    fontSize: 14,
    fontFamily: "system-ui, sans-serif",
    minWidth: 60,
    textAlign: "center" as const,
  },
  zoomLabel: {
    color: "#FFFFFF",
    fontSize: 13,
    fontFamily: "system-ui, sans-serif",
    minWidth: 44,
    textAlign: "center" as const,
  },
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  absoluteFill: {
    ...StyleSheet.absoluteFillObject,
  },
  hiddenVideoView: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
  },
  audioContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  audioTitle: {
    fontSize: 24,
    fontWeight: "600",
    textAlign: "center",
    marginTop: 32,
  },
  pdfOuterContainer: {
    flex: 1,
    backgroundColor: "#1a1a1a",
    position: "relative" as any,
  },
  pdfLoadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    zIndex: 1,
  },
  pdfLoadingText: {
    fontSize: 14,
    marginTop: 12,
  },
  pdfErrorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    padding: 24,
    zIndex: 2,
  },
  pdfErrorTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  pdfErrorText: {
    fontSize: 14,
    textAlign: "center",
  },
  unsupportedContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: 32,
  },
  youtubeStage: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000000",
  },
  topRightRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  compactLoginButton: {
    height: 36,
    borderRadius: 18,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    gap: 6,
  },
  compactLoginLabel: {
    fontSize: 14,
    fontWeight: "500",
  } as TextStyle,
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  embeddingErrorOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 10,
  },
  embeddingErrorCard: {
    borderRadius: 16,
    padding: 24,
    maxWidth: 360,
    width: "100%",
    alignItems: "center",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  embeddingErrorTitle: {
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 10,
  },
  embeddingErrorBody: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 20,
  },
  embeddingErrorActions: {
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  embeddingErrorButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    alignItems: "center",
  },
  embeddingErrorButtonText: {
    fontWeight: "500",
    fontSize: 15,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    zIndex: 1500,
  },
  fullscreenOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 2050,
  },
})

const $unsupportedText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  textAlign: "center",
})
